import React, { useMemo, useEffect, useCallback } from 'react'
import StyleContext, { StyleContextValue } from '../contexts/StyleContext'
import type { Style } from '../types/style.types'
import {
  mergeStyles,
  getStylePropertySource,
  hasStyleProperty,
  styleToCSSVariables,
  cleanupStyleEffects,
} from '../utils/styleUtils'

interface StyleProviderProps {
  children: React.ReactNode
  teamStyle?: Style | null
  eventStyle?: Style | null
  pageStyle?: Style | null
}

/**
 * Main style provider that handles the style hierarchy and provides
 * the merged style to all child components
 */
export const StyleProvider: React.FC<StyleProviderProps> = ({
  children,
  teamStyle,
  eventStyle,
  pageStyle,
}) => {
  // Compute the merged style
  const mergedStyle = useMemo(
    () => mergeStyles(teamStyle, eventStyle, pageStyle),
    [teamStyle, eventStyle, pageStyle]
  )

  // Helper to check if a property is set
  const hasStylePropertyHelper = useCallback(
    (property: keyof Style) => {
      return hasStyleProperty(property, teamStyle, eventStyle, pageStyle)
    },
    [teamStyle, eventStyle, pageStyle]
  )

  // Helper to get the source of a property
  const getStyleSourceHelper = useCallback(
    (property: keyof Style) => {
      return getStylePropertySource(property, teamStyle, eventStyle, pageStyle)
    },
    [teamStyle, eventStyle, pageStyle]
  )

  // Apply CSS variables to the document root
  useEffect(() => {
    if (!mergedStyle) return

    const cssVars = styleToCSSVariables(mergedStyle)
    const root = document.documentElement

    // Apply CSS variables
    Object.entries(cssVars).forEach(([key, value]) => {
      root.style.setProperty(key, value)
    })

    // Apply body styles directly if specified
    if (mergedStyle.bodyColor) {
      document.body.style.color = mergedStyle.bodyColor
    }
    if (mergedStyle.backgroundColor) {
      document.body.style.backgroundColor = mergedStyle.backgroundColor
    }

    // Cleanup on unmount or style change
    return () => {
      cleanupStyleEffects()
      document.body.style.color = ''
      document.body.style.backgroundColor = ''
    }
  }, [mergedStyle])

  // Create dynamic style element for complex styles
  useEffect(() => {
    if (!mergedStyle) return

    const styleElement = document.createElement('style')
    styleElement.setAttribute('data-style-provider', 'true')
    
    const styles: string[] = []

    // Link color
    if (mergedStyle.linkColor) {
      styles.push(`
        a:not(.btn):not(.button) {
          color: ${mergedStyle.linkColor};
        }
        a:not(.btn):not(.button):hover {
          color: ${mergedStyle.linkColor};
          opacity: 0.8;
        }
      `)
    }

    // Button styles
    if (mergedStyle.buttonStyle === 'rounded' && mergedStyle.borderRadius !== undefined) {
      styles.push(`
        .btn, .button, button {
          border-radius: ${mergedStyle.borderRadius}px !important;
        }
      `)
    }

    // Font family
    if (mergedStyle.font) {
      styles.push(`
        body, .font-styled {
          font-family: ${mergedStyle.font}, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }
      `)
    }

    if (styles.length > 0) {
      styleElement.textContent = styles.join('\n')
      document.head.appendChild(styleElement)

      return () => {
        if (styleElement.parentNode) {
          styleElement.parentNode.removeChild(styleElement)
        }
      }
    }
  }, [mergedStyle])

  const contextValue: StyleContextValue = {
    style: mergedStyle,
    eventStyle,
    teamStyle,
    pageStyle,
    hasStyleProperty: hasStylePropertyHelper,
    getStyleSource: getStyleSourceHelper,
  }

  return (
    <StyleContext.Provider value={contextValue}>
      {children}
    </StyleContext.Provider>
  )
}